import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader, StatusBadge } from '../../components/common/PageHeader';
import api, { API_BASE } from '../../lib/api';
import { ArrowLeft, Video, UserCheck, UserMinus, Euro, Save, History, Send, FileText, Receipt, Calendar as CalendarIcon, CalendarPlus, CalendarClock, Clock, PlayCircle, StopCircle, Plus, Trash2, CheckCircle2 as CheckCircle2Icon, ShieldCheck } from 'lucide-react';
import { JOB_STATUSES, MEETING_TOOLS, statusLabel, serviceTypeLabel } from '../../lib/constants';
import { toast } from 'sonner';

const PROVIDER_LABELS = {
  google_meet: 'Google Meet',
  microsoft_teams: 'Microsoft Teams',
  zoom: 'Zoom',
  manual: 'Manueller Link',
};

const AdminJobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [interpreter, setInterpreter] = useState(null);
  const [matching, setMatching] = useState([]);
  const [editing, setEditing] = useState({});
  const [meetingTool, setMeetingTool] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [meetingProvider, setMeetingProvider] = useState('google_meet');
  const [manualLink, setManualLink] = useState('');
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const [sendingLink, setSendingLink] = useState('');
  const [interpreterPrice, setInterpreterPrice] = useState(0);
  const [customerPrice, setCustomerPrice] = useState(93);
  const [status, setStatus] = useState('');
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [syncingCal, setSyncingCal] = useState(false);

  // Gegenvorschlag-Dialog
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposals, setProposals] = useState([{ date: '', time: '', duration: 60, note: '' }]);
  const [proposalMessage, setProposalMessage] = useState('');
  const [proposingLoading, setProposingLoading] = useState(false);

  // Dauer-Erfassung
  const [manualDuration, setManualDuration] = useState('');
  const [durationNote, setDurationNote] = useState('');
  const [completionStatus, setCompletionStatus] = useState('erfolgreich');
  const [technicalIssues, setTechnicalIssues] = useState(false);
  const [technicalIssuesNote, setTechnicalIssuesNote] = useState('');

  useEffect(() => {
    api.get('/oauth/calendar/status').then(r => setCalendarConnected(r.data.connected)).catch(() => {});
    // Show success toast if returning from OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected') === '1') {
      toast.success('Google Kalender verbunden');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('calendar_error')) {
      toast.error(`Google-Verbindung fehlgeschlagen: ${params.get('calendar_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const downloadWithAuth = async (e, url, filename) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('dd_token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error('Download fehlgeschlagen');
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      const res = await api.get('/oauth/calendar/login', { params: { origin: window.location.origin } });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error('Google-Verbindung konnte nicht gestartet werden');
    }
  };

  const syncToGoogleCalendar = async () => {
    setSyncingCal(true);
    try {
      const res = await api.post(`/jobs/${id}/calendar/sync`);
      toast.success('Termin im Google Kalender erstellt');
      if (res.data.html_link) {
        // Open calendar event in new tab
        window.open(res.data.html_link, '_blank');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync fehlgeschlagen');
    } finally {
      setSyncingCal(false);
    }
  };

  const load = async () => {
    try {
      const j = (await api.get(`/jobs/${id}`)).data;
      setJob(j);
      setMeetingTool(j.meeting_tool || '');
      setMeetingLink(j.meeting_link || '');
      setMeetingProvider(j.meeting_provider || 'google_meet');
      setManualLink(j.meeting_link || '');
      setInterpreterPrice(j.interpreter_price || 0);
      setCustomerPrice(j.customer_price || 93);
      setStatus(j.status);

      const [cs, is, ms] = await Promise.all([
        api.get('/customers'),
        api.get('/interpreters'),
        api.get(`/jobs/${id}/matching`),
      ]);
      setCustomer(cs.data.find(c => c.id === j.customer_id));
      setInterpreter(is.data.find(i => i.id === j.interpreter_id));
      setMatching(ms.data);
    } catch (err) {
      toast.error('Fehler beim Laden');
      navigate('/admin/jobs');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const assignInterpreter = async (interpreterId, price) => {
    await api.post(`/jobs/${id}/assign`, { interpreter_id: interpreterId, interpreter_price: price });
    toast.success('Dolmetscher angefragt');
    load();
  };

  const unassignInterpreter = async () => {
    const reason = window.prompt(
      'Zuweisung wirklich aufheben?\n\nOptional: Grund eingeben (wird im Verlauf und in der Benachrichtigung an den Dolmetscher gespeichert).',
      ''
    );
    // null = abgebrochen, "" = bestätigt ohne Grund
    if (reason === null) return;
    try {
      await api.post(`/jobs/${id}/unassign`, { reason });
      toast.success('Zuweisung aufgehoben');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Aufheben fehlgeschlagen');
    }
  };

  const savePricing = async () => {
    await api.put(`/jobs/${id}`, { customer_price: customerPrice, interpreter_price: interpreterPrice });
    toast.success('Preise gespeichert');
    load();
  };

  const createMeeting = async () => {
    setCreatingMeeting(true);
    try {
      const payload = { provider: meetingProvider };
      if (meetingProvider !== 'google_meet') payload.link = manualLink;
      const res = await api.post(`/jobs/${id}/meeting/provider`, payload);
      toast.success(meetingProvider === 'google_meet' ? 'Google Meet erstellt' : 'Meeting-Link gespeichert');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreatingMeeting(false);
    }
  };

  const sendMeetingLink = async (to) => {
    setSendingLink(to);
    try {
      await api.post(`/jobs/${id}/meeting/send`, { to });
      toast.success(`Link an ${to === 'customer' ? 'Kunde' : to === 'interpreter' ? 'Dolmetscher' : 'Kunde & Dolmetscher'} gesendet`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSendingLink('');
    }
  };

  const saveMeeting = async () => {
    // Kept for compat but no longer called from UI
    await api.put(`/jobs/${id}`, { meeting_tool: meetingTool, meeting_link: meetingLink });
    toast.success('Meetinglink gespeichert');
    load();
  };

  const changeStatus = async (newStatus) => {
    await api.put(`/jobs/${id}`, { status: newStatus });
    toast.success('Status aktualisiert');
    load();
  };

  const sendProposals = async () => {
    const valid = proposals.filter(p => p.date && p.time);
    if (valid.length === 0) {
      toast.error('Bitte mindestens einen vollständigen Terminvorschlag eintragen');
      return;
    }
    setProposingLoading(true);
    try {
      await api.post(`/jobs/${id}/propose`, {
        proposals: valid.map(p => ({ date: p.date, time: p.time, duration: parseInt(p.duration) || 60, note: p.note || '' })),
        message: proposalMessage,
      });
      toast.success(`${valid.length} Terminvorschläge an Kunden gesendet`);
      setProposeOpen(false);
      setProposals([{ date: '', time: '', duration: 60, note: '' }]);
      setProposalMessage('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setProposingLoading(false);
    }
  };

  const saveDuration = async () => {
    const mins = parseInt(manualDuration);
    if (!mins || mins < 1) {
      toast.error('Bitte gültige Dauer in Minuten eingeben');
      return;
    }
    try {
      const res = await api.post(`/jobs/${id}/meeting/duration`, {
        actual_duration_minutes: mins,
        note: durationNote,
        completion_status: completionStatus,
        technical_issues: technicalIssues,
        technical_issues_note: technicalIssues ? technicalIssuesNote : '',
      });
      toast.success(`Abgerechnet: ${res.data.billed_duration_minutes} Min / ${res.data.billed_customer_amount} €`);
      setManualDuration('');
      setDurationNote('');
      setTechnicalIssues(false);
      setTechnicalIssuesNote('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    }
  };

  const setPricingModel = async (model) => {
    try {
      await api.put(`/jobs/${id}`, { pricing_model: model });
      toast.success(`Abrechnungsmodell: ${model === 'behoerde' ? 'Behörden/Justiz' : 'Privat'}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Wechseln');
    }
  };

  if (!job) return <AppLayout><div>Lade...</div></AppLayout>;

  const margin = (customerPrice - interpreterPrice).toFixed(2);

  return (
    <AppLayout>
      <Link to="/admin/jobs" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4" data-testid="back-to-jobs">
        <ArrowLeft className="w-4 h-4" /> Zurück zu Aufträgen
      </Link>

      <PageHeader title={`Auftrag ${job.id.slice(0, 16)}`} description={customer?.organization_name}>
        <StatusBadge status={job.status} label={statusLabel(job.status)} />
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-md card-subtle p-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-4">Auftragsdetails</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">Auftrags-Nr.</div><div className="text-slate-900 mt-1 font-mono">{job.job_number || job.id.slice(0, 16)}</div></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">Kunde</div><div className="text-slate-900 mt-1">{customer?.organization_name} ({customer?.contact_person})</div></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">Sprache</div><div className="text-slate-900 mt-1">{job.source_language} → {job.target_language}</div></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">Einsatzart</div><div className="text-slate-900 mt-1">{serviceTypeLabel(job.service_type)}</div></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">Fachgebiet</div><div className="text-slate-900 mt-1">{job.specialization || '-'}</div></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">Datum / Uhrzeit</div><div className="text-slate-900 mt-1">{job.date} {job.time}</div></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">Dauer</div><div className="text-slate-900 mt-1">{job.duration} Min. <span className="text-slate-400 text-xs">(Mind. {job.min_duration || job.duration})</span></div></div>
              <div className="col-span-2">
                <div className="text-xs text-slate-500 uppercase tracking-wider">Abrechnungsmodell</div>
                <div className="mt-1 flex items-center gap-2 flex-wrap" data-testid="pricing-model-badge">
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${(job.pricing_model || 'privat') === 'behoerde' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-slate-100 text-slate-700 border border-slate-200'}`}>
                    <ShieldCheck className="w-3 h-3" />
                    {(job.pricing_model || 'privat') === 'behoerde' ? 'Behörden/Justiz · 15-Min-Pauschale + min.genau' : 'Privat · Aufrundung auf volle Stunde'}
                  </span>
                  {!job.billed_duration_minutes && (
                    <button
                      type="button"
                      onClick={() => setPricingModel(job.pricing_model === 'behoerde' ? 'privat' : 'behoerde')}
                      data-testid="toggle-pricing-model-btn"
                      className="text-xs text-[#0F4C81] hover:underline"
                    >
                      Wechseln zu {job.pricing_model === 'behoerde' ? 'Privat' : 'Behörden'}
                    </button>
                  )}
                </div>
              </div>
              {job.location && <div className="col-span-2"><div className="text-xs text-slate-500 uppercase tracking-wider">Einsatzort</div><div className="text-slate-900 mt-1">{job.location}</div></div>}
              {job.notes && <div className="col-span-2"><div className="text-xs text-slate-500 uppercase tracking-wider">Bemerkungen</div><div className="text-slate-900 mt-1 whitespace-pre-wrap">{job.notes}</div></div>}
            </div>
            {(job.status === 'neu' || job.status === 'in_bearbeitung') && (
              <div className="mt-4 pt-4 border-t border-slate-200 flex gap-2">
                <button
                  onClick={() => setProposeOpen(true)}
                  data-testid="open-propose-btn"
                  className="inline-flex items-center gap-2 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 font-medium rounded-md px-3 py-2 text-sm"
                >
                  <CalendarClock className="w-4 h-4" /> Gegenvorschlag senden
                </button>
              </div>
            )}
            {job.status === 'gegenvorschlag_gesendet' && job.proposals?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-2">Gesendete Terminvorschläge (wartend auf Kunde)</div>
                <div className="space-y-1">
                  {job.proposals.map((p, i) => (
                    <div key={p.id} className="text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                      <strong>Vorschlag {i+1}:</strong> {p.date} um {p.time} ({p.duration} Min){p.note ? ` — ${p.note}` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {job.proposal_response && (
              <div className={`mt-3 text-sm rounded px-3 py-2 ${job.proposal_response.action === 'accept' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
                <strong>Kundenantwort:</strong> {job.proposal_response.action === 'accept' ? 'Termin angenommen' : 'Alle Vorschläge abgelehnt'}
                {job.proposal_response.message && <div className="text-xs mt-1 italic">{job.proposal_response.message}</div>}
              </div>
            )}
          </div>

          {/* Matching */}
          <div className="bg-white border border-slate-200 rounded-md card-subtle p-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-4 flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-[#0F4C81]" /> Passende Dolmetscher ({matching.length})
            </h3>
            {interpreter ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-4 mb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Aktuell zugewiesen</div>
                    <div className="mt-1 text-slate-900 font-medium flex items-center gap-2 flex-wrap">
                      {interpreter.salutation ? `${interpreter.salutation} ` : ''}{interpreter.first_name} {interpreter.last_name}
                      {interpreter.sworn && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold bg-[#0F4C81] text-white rounded px-2 py-0.5" title="Allgemein beeidigt / vereidigt">
                          <ShieldCheck className="w-3 h-3" /> Vereidigt
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-600">{interpreter.email} · {interpreter.phone}</div>
                  </div>
                  {!['abgeschlossen', 'in_durchfuehrung'].includes(job.status) && (
                    <button
                      type="button"
                      onClick={unassignInterpreter}
                      data-testid="unassign-interpreter-btn"
                      title="Zuweisung aufheben"
                      className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium border border-red-200 text-red-700 hover:bg-red-50 hover:border-red-300 rounded-md px-3 py-1.5 transition-colors"
                    >
                      <UserMinus className="w-3.5 h-3.5" />
                      Zuweisung aufheben
                    </button>
                  )}
                </div>
              </div>
            ) : null}
            {matching.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4">Keine passenden Dolmetscher gefunden</div>
            ) : (
              <div className="space-y-2">
                {matching.map(i => (
                  <div key={i.id} className={`flex items-center justify-between p-3 border rounded hover:bg-slate-50 ${i.sworn ? 'border-[#0F4C81]/40 bg-[#0F4C81]/[0.03]' : 'border-slate-200'}`} data-testid={`matching-row-${i.id}`}>
                    <div>
                      <div className="text-sm font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                        <span>{i.salutation ? `${i.salutation} ` : ''}{i.first_name} {i.last_name}</span>
                        {i.sworn && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-[#0F4C81] text-white rounded px-1.5 py-0.5" data-testid={`sworn-badge-${i.id}`} title="Allgemein beeidigt / vereidigt">
                            <ShieldCheck className="w-3 h-3" /> Vereidigt
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {(i.language_pairs && i.language_pairs.length > 0) ? (
                          <span>{i.language_pairs.map(p => `${p.source} ↔ ${p.target}`).join(' · ')}</span>
                        ) : (
                          <span>{i.languages.join(', ')} → {i.target_languages.join(', ')}</span>
                        )}
                        {' · '}{i.specializations.join(', ') || 'Keine Fachgebiete'}
                      </div>
                      <div className="text-xs text-slate-700 font-mono mt-1">{i.custom_rate.toFixed(2)} €/Std</div>
                    </div>
                    <button
                      onClick={() => assignInterpreter(i.id, i.custom_rate)}
                      disabled={job.interpreter_id === i.id}
                      data-testid={`assign-${i.id}`}
                      className="text-xs bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {job.interpreter_id === i.id ? 'Zugewiesen' : 'Anfragen'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* History */}
          <div className="bg-white border border-slate-200 rounded-md card-subtle p-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-4 flex items-center gap-2">
              <History className="w-4 h-4 text-[#0F4C81]" /> Verlauf
            </h3>
            <div className="space-y-2 text-sm">
              {(job.history || []).slice().reverse().map((h, i) => (
                <div key={`${h.at}-${h.action}-${i}`} className="flex gap-3 pb-2 border-b border-slate-100 last:border-0">
                  <div className="text-xs text-slate-400 font-mono w-32 flex-shrink-0">{new Date(h.at).toLocaleString('de-DE')}</div>
                  <div className="text-slate-700">{h.action} {h.details && <span className="text-slate-500">— {h.details}</span>}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <div className="bg-white border border-slate-200 rounded-md card-subtle p-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-3">Status</h3>
            <select value={status} onChange={(e) => { setStatus(e.target.value); changeStatus(e.target.value); }} data-testid="job-status-select" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
              {JOB_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {/* Pricing */}
          <div className="bg-white border border-slate-200 rounded-md card-subtle p-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-4 flex items-center gap-2">
              <Euro className="w-4 h-4 text-[#0F4C81]" /> Preislogik
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Verkaufspreis (Kunde)</label>
                <input type="number" step="0.01" value={customerPrice} onChange={(e) => setCustomerPrice(parseFloat(e.target.value) || 0)} data-testid="customer-price-input" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Einkaufspreis (Dolmetscher)</label>
                <input type="number" step="0.01" value={interpreterPrice} onChange={(e) => setInterpreterPrice(parseFloat(e.target.value) || 0)} data-testid="interpreter-price-input" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div className="flex items-center justify-between py-2 border-t border-slate-200">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Marge</span>
                <span className={`font-mono font-semibold ${margin >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="margin-display">{margin} €</span>
              </div>
              <button onClick={savePricing} data-testid="save-pricing-btn" className="w-full bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm inline-flex items-center justify-center gap-2">
                <Save className="w-4 h-4" /> Preise speichern
              </button>
            </div>
          </div>

          {/* Meeting Provider */}
          <div className="bg-white border border-slate-200 rounded-md card-subtle p-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-4 flex items-center gap-2">
              <Video className="w-4 h-4 text-[#0F4C81]" /> Meeting
            </h3>

            {job.meeting_link ? (
              <div className="space-y-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
                  <div className="flex items-center gap-2 mb-1 font-semibold text-emerald-800">
                    <CheckCircle2Icon className="w-4 h-4" />
                    {PROVIDER_LABELS[job.meeting_provider] || job.meeting_provider || 'Meeting'} erstellt
                  </div>
                  <a href={job.meeting_link} target="_blank" rel="noreferrer" className="text-[#0F4C81] hover:underline break-all text-xs" data-testid="current-meeting-link">
                    {job.meeting_link}
                  </a>
                  {job.meeting_created_at && (
                    <div className="text-xs text-slate-500 mt-1">
                      Erstellt: {new Date(job.meeting_created_at).toLocaleString('de-DE')}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Link versenden an:</div>
                  <div className="grid grid-cols-1 gap-2">
                    <button onClick={() => sendMeetingLink('customer')} disabled={sendingLink === 'customer'} data-testid="send-to-customer-btn" className="text-sm border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 rounded-md px-3 py-2 inline-flex items-center justify-center gap-2 disabled:opacity-50">
                      <Send className="w-3.5 h-3.5" /> An Kunde senden
                      {job.meeting_sent_to_customer && <span className="text-emerald-600 text-xs ml-auto">✓ gesendet</span>}
                    </button>
                    <button onClick={() => sendMeetingLink('interpreter')} disabled={sendingLink === 'interpreter'} data-testid="send-to-interpreter-btn" className="text-sm border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 rounded-md px-3 py-2 inline-flex items-center justify-center gap-2 disabled:opacity-50">
                      <Send className="w-3.5 h-3.5" /> An Dolmetscher senden
                      {job.meeting_sent_to_interpreter && <span className="text-emerald-600 text-xs ml-auto">✓ gesendet</span>}
                    </button>
                    <button onClick={() => sendMeetingLink('both')} disabled={sendingLink === 'both'} data-testid="send-to-both-btn" className="text-sm bg-[#001d4c] hover:bg-[#0A365C] text-white rounded-md px-3 py-2 inline-flex items-center justify-center gap-2 disabled:opacity-50 font-medium">
                      <Send className="w-3.5 h-3.5" /> An beide senden
                    </button>
                  </div>
                </div>

                <button onClick={() => { setMeetingProvider('google_meet'); setManualLink(''); setJob({...job, meeting_link: ''}); }} className="w-full text-xs text-slate-500 hover:text-[#0F4C81] text-center mt-2">
                  Meeting ersetzen / neu erstellen
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Meeting-Anbieter</label>
                  <select value={meetingProvider} onChange={(e) => setMeetingProvider(e.target.value)} data-testid="meeting-provider-select" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
                    <option value="google_meet">Google Meet (empfohlen, automatisch)</option>
                    <option value="microsoft_teams">Microsoft Teams (Link manuell)</option>
                    <option value="zoom">Zoom (Link manuell)</option>
                    <option value="manual">Manueller Link</option>
                  </select>
                </div>
                {meetingProvider !== 'google_meet' && (
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Meeting-Link</label>
                    <input type="url" value={manualLink} onChange={(e) => setManualLink(e.target.value)} placeholder="https://..." data-testid="manual-meeting-link-input" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                  </div>
                )}
                <button
                  onClick={createMeeting}
                  disabled={creatingMeeting || (meetingProvider !== 'google_meet' && !manualLink)}
                  data-testid="create-meeting-btn"
                  className="w-full bg-[#001d4c] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Video className="w-4 h-4" />
                  {creatingMeeting ? 'Wird erstellt...' : meetingProvider === 'google_meet' ? 'Google-Meet-Link erstellen' : 'Link speichern'}
                </button>
                {meetingProvider === 'google_meet' && !calendarConnected && (
                  <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                    ℹ️ Für automatische Google Meet-Erstellung muss Ihr Google-Kalender verbunden sein.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Meeting-Durchführung & Abrechnung */}
          <div className="bg-white border border-slate-200 rounded-md card-subtle p-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#0F4C81]" /> Dauer & Abrechnung
            </h3>
            {job.actual_start_time && (
              <div className="text-xs text-slate-600 mb-3 space-y-1">
                <div><span className="text-slate-400">Start:</span> {new Date(job.actual_start_time).toLocaleString('de-DE')}</div>
                {job.actual_end_time && <div><span className="text-slate-400">Ende:</span> {new Date(job.actual_end_time).toLocaleString('de-DE')}</div>}
                {job.actual_duration_minutes && <div><span className="text-slate-400">Dauer:</span> {job.actual_duration_minutes} Min (tatsächlich)</div>}
              </div>
            )}
            {job.billed_duration_minutes ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 mb-3 text-sm space-y-1">
                <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Abgerechnet</div>
                <div className="flex justify-between"><span>Dauer:</span><span className="font-mono">{job.billed_duration_minutes} Min</span></div>
                <div className="flex justify-between"><span>Kunde:</span><span className="font-mono font-semibold">{job.billed_customer_amount?.toFixed(2)} €</span></div>
                <div className="flex justify-between"><span>Dolmetscher:</span><span className="font-mono">{job.billed_interpreter_amount?.toFixed(2)} €</span></div>
                {job.completion_status && (
                  <div className="flex justify-between pt-1 border-t border-emerald-200 mt-1">
                    <span>Abschluss:</span>
                    <span className={`font-medium ${job.completion_status === 'abgebrochen' ? 'text-red-700' : 'text-emerald-700'}`}>
                      {job.completion_status === 'abgebrochen' ? 'Abgebrochen' : 'Erfolgreich'}
                    </span>
                  </div>
                )}
                {job.technical_issues && (
                  <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
                    <div className="text-xs font-semibold text-amber-800">⚠ Technische Probleme dokumentiert</div>
                    {job.technical_issues_note && <div className="text-xs text-amber-700 mt-0.5">{job.technical_issues_note}</div>}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Tatsächliche Dauer (Min.)</label>
                  <input type="number" min="1" value={manualDuration} onChange={(e) => setManualDuration(e.target.value)} placeholder="z.B. 75" data-testid="manual-duration-input" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                  <div className="text-xs text-slate-400 mt-1">
                    Mindest: {job.min_duration || job.duration} Min ·
                    {(job.pricing_model || 'privat') === 'behoerde'
                      ? ' Behörden: 15-Min-Pauschale + minutengenau'
                      : ' Privat: Aufrundung auf volle Stunde'}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Einsatz-Status</label>
                  <div className="flex gap-2" data-testid="completion-status-group">
                    {[['erfolgreich', 'Erfolgreich'], ['abgebrochen', 'Abgebrochen']].map(([v, l]) => (
                      <button
                        key={v} type="button"
                        onClick={() => setCompletionStatus(v)}
                        data-testid={`completion-${v}`}
                        className={`flex-1 py-2 text-sm font-medium rounded-md border transition-colors ${completionStatus === v ? (v === 'abgebrochen' ? 'bg-red-600 text-white border-red-600' : 'bg-emerald-600 text-white border-emerald-600') : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                      >{l}</button>
                    ))}
                  </div>
                </div>
                <div className="border border-slate-200 rounded-md p-3 bg-slate-50 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={technicalIssues}
                      onChange={(e) => setTechnicalIssues(e.target.checked)}
                      data-testid="technical-issues-checkbox"
                      className="w-4 h-4 rounded border-slate-300 text-[#0F4C81] focus:ring-[#0F4C81]"
                    />
                    <span className="text-sm font-medium text-slate-800">Technische Probleme während des Einsatzes</span>
                  </label>
                  {technicalIssues && (
                    <textarea
                      rows="2"
                      value={technicalIssuesNote}
                      onChange={(e) => setTechnicalIssuesNote(e.target.value)}
                      placeholder="Kurze Beschreibung (z.B. Verbindungsabbrüche, Audio-Probleme)..."
                      data-testid="technical-issues-note"
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none"
                    />
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Notiz (optional)</label>
                  <input value={durationNote} onChange={(e) => setDurationNote(e.target.value)} data-testid="duration-note-input" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
                <button onClick={saveDuration} disabled={!manualDuration} data-testid="save-duration-btn" className="w-full bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  <Save className="w-4 h-4" /> Dauer speichern & abrechnen
                </button>
              </div>
            )}
          </div>

          {/* Downloads & Calendar */}
          <div className="bg-white border border-slate-200 rounded-md card-subtle p-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#0F4C81]" /> Downloads
            </h3>
            <div className="space-y-2">
              <button
                onClick={(e) => downloadWithAuth(e, `${API_BASE}/jobs/${id}/pdf/confirmation`, `Auftragsbestaetigung_${(job?.job_number || id).replace(/[^a-z0-9\-]/gi,'_')}.pdf`)}
                data-testid="download-confirmation-btn"
                className="w-full flex items-center gap-2 text-sm border border-slate-200 hover:border-[#0F4C81] hover:bg-slate-50 rounded-md px-3 py-2 transition-colors text-slate-700"
              >
                <FileText className="w-4 h-4 text-slate-400" /> Auftragsbestätigung (PDF)
              </button>
              <button
                onClick={(e) => downloadWithAuth(e, `${API_BASE}/jobs/${id}/pdf/invoice`, `Rechnung_${(job?.job_number || id).replace(/[^a-z0-9\-]/gi,'_')}.pdf`)}
                data-testid="download-invoice-btn"
                className="w-full flex items-center gap-2 text-sm border border-slate-200 hover:border-[#0F4C81] hover:bg-slate-50 rounded-md px-3 py-2 transition-colors text-slate-700"
              >
                <Receipt className="w-4 h-4 text-slate-400" /> Rechnung (PDF)
              </button>
              <button
                onClick={(e) => downloadWithAuth(e, `${API_BASE}/jobs/${id}/pdf/protocol`, `Einsatzprotokoll_${(job?.job_number || id).replace(/[^a-z0-9\-]/gi,'_')}.pdf`)}
                data-testid="download-protocol-btn"
                className="w-full flex items-center gap-2 text-sm border border-slate-200 hover:border-[#0F4C81] hover:bg-slate-50 rounded-md px-3 py-2 transition-colors text-slate-700"
              >
                <ShieldCheck className="w-4 h-4 text-slate-400" /> Einsatzprotokoll (PDF)
              </button>
              <button
                onClick={(e) => downloadWithAuth(e, `${API_BASE}/jobs/${id}/calendar.ics`, `dolmetschtermin_${(job?.job_number || id).replace(/[^a-z0-9\-]/gi,'_')}.ics`)}
                data-testid="download-ics-btn"
                className="w-full flex items-center gap-2 text-sm border border-slate-200 hover:border-[#0F4C81] hover:bg-slate-50 rounded-md px-3 py-2 transition-colors text-slate-700"
              >
                <CalendarIcon className="w-4 h-4 text-slate-400" /> .ics Kalender-Datei
              </button>
              {calendarConnected ? (
                <button
                  onClick={syncToGoogleCalendar}
                  disabled={syncingCal}
                  data-testid="sync-google-calendar-btn"
                  className="w-full flex items-center justify-center gap-2 text-sm bg-[#0F4C81]/5 border border-[#0F4C81]/30 hover:bg-[#0F4C81]/10 rounded-md px-3 py-2 transition-colors text-[#0F4C81] font-medium disabled:opacity-50"
                >
                  <CalendarPlus className="w-4 h-4" />
                  {syncingCal ? 'Wird übertragen...' : 'In Google Kalender eintragen'}
                </button>
              ) : (
                <button
                  onClick={connectGoogleCalendar}
                  data-testid="connect-google-calendar-btn"
                  className="w-full flex items-center justify-center gap-2 text-sm bg-white border border-slate-300 hover:border-[#0F4C81] hover:bg-slate-50 rounded-md px-3 py-2 transition-colors text-slate-700"
                >
                  <CalendarPlus className="w-4 h-4 text-[#0F4C81]" /> Google Kalender verbinden
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gegenvorschlag-Dialog */}
      {proposeOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setProposeOpen(false)}>
          <div className="bg-white rounded-md max-w-2xl w-full p-6 my-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">Terminvorschläge senden</h3>
            <p className="text-sm text-slate-500 mb-4">Der Kunde erhält eine E-Mail mit allen Vorschlägen und kann einen auswählen.</p>

            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
              {proposals.map((p, i) => (
                <div key={i} className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vorschlag {i+1}</span>
                    {proposals.length > 1 && (
                      <button onClick={() => setProposals(ps => ps.filter((_, idx) => idx !== i))} data-testid={`remove-proposal-${i}`} className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> Entfernen
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <input type="date" value={p.date} onChange={(e) => setProposals(ps => ps.map((x, idx) => idx === i ? { ...x, date: e.target.value } : x))} data-testid={`proposal-date-${i}`} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                    <input type="time" value={p.time} onChange={(e) => setProposals(ps => ps.map((x, idx) => idx === i ? { ...x, time: e.target.value } : x))} data-testid={`proposal-time-${i}`} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                    <input type="number" min="15" step="15" value={p.duration} onChange={(e) => setProposals(ps => ps.map((x, idx) => idx === i ? { ...x, duration: parseInt(e.target.value) || 60 } : x))} placeholder="Min." className="border border-slate-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                  </div>
                  <input value={p.note} onChange={(e) => setProposals(ps => ps.map((x, idx) => idx === i ? { ...x, note: e.target.value } : x))} placeholder="Notiz (optional)" className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
              ))}
            </div>

            <button
              onClick={() => setProposals(ps => [...ps, { date: '', time: '', duration: 60, note: '' }])}
              data-testid="add-proposal-btn"
              className="mt-2 w-full border border-dashed border-slate-300 hover:border-[#0F4C81] hover:bg-slate-50 rounded-md px-3 py-2 text-sm text-slate-600 inline-flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" /> Weiteren Vorschlag hinzufügen
            </button>

            <div className="mt-4">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Nachricht an Kunden (optional)</label>
              <textarea rows="3" value={proposalMessage} onChange={(e) => setProposalMessage(e.target.value)} data-testid="proposal-message" placeholder="z.B. Grund der Verschiebung..." className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
            </div>

            <div className="flex gap-2 pt-4 justify-end">
              <button type="button" onClick={() => setProposeOpen(false)} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Abbrechen</button>
              <button type="button" onClick={sendProposals} disabled={proposingLoading} data-testid="send-proposals-btn" className="bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm disabled:opacity-50 inline-flex items-center gap-2">
                <Send className="w-4 h-4" /> {proposingLoading ? 'Sende...' : 'Vorschläge senden'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default AdminJobDetail;